// ========================================
// MIGLIORAMENTO: Gestione Intelligente Inserimento Personale
// src/services/activeShiftChecker.js
// ========================================
// 
// ✅ Controlla se c'è un turno attivo (ingresso senza uscita)
// ✅ Decide quando chiedere inserimento manuale ore
// ✅ Permette inserimento senza prompt se turno in corso
//

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * 🔍 Verifica se un utente ha un turno attivo per una data specifica
 * 
 * @param {string} userId - ID dell'utente
 * @param {string} date - Data nel formato YYYY-MM-DD
 * @returns {Promise<Object>} - Stato del turno
 */
export const checkActiveShift = async (userId, date) => {
  try {
    console.log(`🔍 Controllo turno attivo per user ${userId} data ${date}`);
    
    // Cerca timbrature attive per questa data
    const timekeepingRef = collection(db, 'timekeeping');
    const q = query(
      timekeepingRef,
      where('userId', '==', userId),
      where('date', '==', date),
      where('status', '==', 'in-progress') // Turno ancora aperto
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`❌ Nessun turno attivo trovato per ${date}`);
      return {
        hasActiveShift: false,
        reason: 'no_active_shift'
      };
    }
    
    // Prendi il primo turno attivo (dovrebbe essere uno solo)
    const shiftDoc = snapshot.docs[0];
    const shiftData = shiftDoc.data();
    
    console.log(`✅ Turno attivo trovato:`, {
      clockInTime: shiftData.clockInTime,
      date: shiftData.date,
      status: shiftData.status
    });
    
    return {
      hasActiveShift: true,
      shiftData: {
        id: shiftDoc.id,
        clockInTime: shiftData.clockInTime,
        date: shiftData.date,
        status: shiftData.status
      },
      reason: 'active_shift_found'
    };
    
  } catch (error) {
    console.error('❌ Errore controllo turno attivo:', error);
    return {
      hasActiveShift: false,
      reason: 'error',
      error: error.message
    };
  }
};

/**
 * 🎯 Determina se richiedere inserimento manuale ore
 * 
 * Logica:
 * 1. Se ci sono ore nel WorkHours → NO (usa quelle)
 * 2. Se c'è turno attivo (ingresso senza uscita) → NO (aspetta timbratura)
 * 3. Se non ci sono ne ore ne turno attivo → SÌ (chiedi inserimento manuale)
 * 
 * @param {string} userId - ID dell'utente
 * @param {string} date - Data nel formato YYYY-MM-DD
 * @param {Object} workHoursData - Dati dalle ore lavorate (getWorkHoursForDate)
 * @returns {Promise<Object>} - Decisione su inserimento manuale
 */
export const shouldRequestManualHours = async (userId, date, workHoursData) => {
  console.log(`🤔 Valutazione necessità inserimento manuale per ${userId} data ${date}`);
  
  // CASO 1: Ore trovate nel WorkHours
  if (workHoursData && workHoursData.found && !workHoursData.isSpecialDay) {
    console.log(`✅ Ore trovate in WorkHours: ${workHoursData.oreLavorate}h`);
    return {
      needsManualInput: false,
      reason: 'hours_found_in_workhours',
      message: 'Ore già registrate nel sistema',
      hoursData: workHoursData
    };
  }
  
  // CASO 2: Giorno speciale (Ferie, Malattia, Permesso)
  if (workHoursData && workHoursData.isSpecialDay) {
    console.log(`📅 Giorno speciale: ${workHoursData.specialCode}`);
    return {
      needsManualInput: false,
      reason: 'special_day',
      message: workHoursData.specialDescription,
      hoursData: workHoursData
    };
  }
  
  // CASO 3: Controlla se c'è un turno attivo
  const activeShiftStatus = await checkActiveShift(userId, date);
  
  if (activeShiftStatus.hasActiveShift) {
    console.log(`⏳ Turno attivo trovato - ingresso senza uscita`);
    return {
      needsManualInput: false,
      reason: 'active_shift',
      message: `Turno in corso (ingresso: ${activeShiftStatus.shiftData.clockInTime})`,
      shiftData: activeShiftStatus.shiftData,
      suggestion: 'Le ore verranno recuperate automaticamente dopo la timbratura di uscita'
    };
  }
  
  // CASO 4: Nessuna ora e nessun turno attivo
  console.log(`❌ Nessuna ora trovata e nessun turno attivo - richiesta inserimento manuale`);
  return {
    needsManualInput: true,
    reason: 'no_hours_and_no_shift',
    message: 'Nessuna timbratura trovata per questa data',
    suggestion: 'Inserisci le ore manualmente o aggiungi con 0 ore e sincronizza dopo'
  };
};

/**
 * 📊 Ottieni informazioni complete sullo stato delle ore per un utente
 * Funzione completa che combina tutto
 * 
 * @param {string} userId - ID dell'utente
 * @param {string} date - Data nel formato YYYY-MM-DD
 * @returns {Promise<Object>} - Stato completo
 */
export const getHoursStatusForUser = async (userId, date) => {
  try {
    console.log(`📊 Recupero stato completo ore per ${userId} data ${date}`);
    
    // 1. Controlla WorkHours
    const { getWorkHoursForDate } = await import('./workHoursIntegrationService');
    const workHoursData = await getWorkHoursForDate(userId, date, false, null);
    
    // 2. Controlla turno attivo
    const activeShiftStatus = await checkActiveShift(userId, date);
    
    // 3. Determina necessità inserimento manuale
    const manualInputDecision = await shouldRequestManualHours(userId, date, workHoursData);
    
    return {
      success: true,
      workHours: workHoursData,
      activeShift: activeShiftStatus,
      manualInput: manualInputDecision,
      summary: {
        hasHours: workHoursData?.found || false,
        hasActiveShift: activeShiftStatus.hasActiveShift,
        needsManualInput: manualInputDecision.needsManualInput,
        canAddDirectly: !manualInputDecision.needsManualInput
      }
    };
    
  } catch (error) {
    console.error('❌ Errore recupero stato ore:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * 🔔 Genera messaggio user-friendly basato sullo stato
 * 
 * @param {Object} hoursStatus - Risultato di getHoursStatusForUser
 * @returns {string} - Messaggio da mostrare all'utente
 */
export const getUserFriendlyMessage = (hoursStatus) => {
  if (!hoursStatus.success) {
    return '❌ Errore nel recupero delle informazioni. Riprova.';
  }
  
  const { summary, workHours, activeShift, manualInput } = hoursStatus;
  
  // Ore trovate
  if (summary.hasHours && !workHours.isSpecialDay) {
    return `✅ Ore trovate: ${workHours.oreLavorate}h (${workHours.oreStandard}h std + ${workHours.oreStraordinario}h straord)`;
  }
  
  // Giorno speciale
  if (workHours?.isSpecialDay) {
    return `📅 ${workHours.specialDescription}`;
  }
  
  // Turno attivo
  if (summary.hasActiveShift) {
    return `⏳ Turno in corso (ingresso: ${activeShift.shiftData.clockInTime})\n` +
           `Le ore verranno calcolate dopo la timbratura di uscita`;
  }
  
  // Nessuna informazione
  if (summary.needsManualInput) {
    return `❌ Nessuna timbratura trovata\n` +
           `Vuoi inserire le ore manualmente o aggiungere con 0 ore?`;
  }
  
  return '📊 Stato ore non determinato';
};